/* LIQUID_GOLD: Shared scripts for all Liquid Gold sections (homepage + PDP).
   Single file, single namespace. Each module is idempotent — calling init()
   multiple times on the same element is safe (guarded with data-lg-inited). */
(function () {
  'use strict';

  window.LiquidGold = window.LiquidGold || {};

  function once(el) {
    if (el.dataset.lgInited) return false;
    el.dataset.lgInited = '1';
    return true;
  }

  // 1) Scroll-reveal: add .is-visible to .lg-reveal elements when they enter the viewport.
  window.LiquidGold.reveal = {
    init: function (root) {
      var scope = root || document;
      var els = scope.querySelectorAll('.lg-reveal');
      if (!els.length) return;
      if (typeof IntersectionObserver === 'undefined') {
        Array.prototype.forEach.call(els, function (el) { el.classList.add('is-visible'); });
        return;
      }
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
      Array.prototype.forEach.call(els, function (el) {
        if (!once(el)) return;
        observer.observe(el);
      });
    }
  };

  // 2) Hero video controls.
  window.LiquidGold.hero = {
    init: function (root) {
      var scope = root || document;
      Array.prototype.forEach.call(scope.querySelectorAll('[data-lg-hero]'), function (hero) {
        if (!once(hero)) return;
        var video = hero.querySelector('video');
        if (!video) return;
        var playBtn = hero.querySelector('[data-lg-hero-play]');
        var muteBtn = hero.querySelector('[data-lg-hero-mute]');

        if (playBtn) {
          playBtn.addEventListener('click', function () {
            if (video.paused) {
              var p = video.play();
              if (p && typeof p.then === 'function') { p.catch(function () {}); }
              playBtn.setAttribute('aria-pressed', 'false');
            } else {
              video.pause();
              playBtn.setAttribute('aria-pressed', 'true');
            }
          });
        }
        if (muteBtn) {
          muteBtn.addEventListener('click', function () {
            video.muted = !video.muted;
            muteBtn.setAttribute('aria-pressed', String(!video.muted));
          });
        }
      });
    }
  };

  // 3) Dose slider — compute scoop count + pouch days from weight.
  window.LiquidGold.doseSlider = {
    compute: function (lb) {
      var scoops;
      if (lb <= 25) { scoops = '1 scoop'; }
      else if (lb <= 55) { scoops = '2 scoops'; }
      else if (lb <= 85) { scoops = '2–3 scoops'; }
      else { scoops = '3–4 scoops'; }
      var dailyG = Math.max(1, Math.round(5 + lb * 0.1));
      var days = Math.max(1, Math.round(290 / dailyG));
      var helperText = '~' + Math.max(1, days - 3) + '–' + (days + 3) + ' days per 290g pouch · ' + Math.max(1, dailyG - 2) + '–' + (dailyG + 2) + ' g daily.';
      return { scoops: scoops, helperText: helperText, days: days, dailyG: dailyG };
    },
    init: function (root) {
      var scope = root || document;
      var self = this;
      Array.prototype.forEach.call(scope.querySelectorAll('[data-lg-dose]'), function (el) {
        if (!once(el)) return;
        var slider = el.querySelector('input[type="range"]');
        var readout = el.querySelector('[data-lg-dose-readout]');
        var result = el.querySelector('[data-lg-dose-result]');
        var helper = el.querySelector('[data-lg-dose-helper]');
        if (!slider) return;

        var update = function () {
          var lb = Number(slider.value);
          var data = self.compute(lb);
          if (readout) { readout.textContent = String(lb); }
          if (result) { result.textContent = data.scoops; }
          if (helper) { helper.textContent = data.helperText; }
        };

        slider.addEventListener('input', function () {
          if (window.requestAnimationFrame) { window.requestAnimationFrame(update); } else { update(); }
        });
        update();
      });
    }
  };

  // 4) UGC carousel: prev/next + tap-to-play/pause + arrow disabled state.
  window.LiquidGold.ugc = {
    init: function (root) {
      var scope = root || document;
      Array.prototype.forEach.call(scope.querySelectorAll('[data-lg-ugc]'), function (el) {
        if (!once(el)) return;
        var track = el.querySelector('[data-lg-ugc-track]');
        var prev = el.querySelector('[data-lg-ugc-prev]');
        var next = el.querySelector('[data-lg-ugc-next]');
        if (!track) return;

        var cardWidth = function () {
          var card = track.querySelector('li');
          if (!card) return 360;
          var rect = card.getBoundingClientRect();
          var style = window.getComputedStyle(track);
          var gap = parseInt(style.columnGap || style.gap || '24', 10) || 24;
          return rect.width + gap;
        };

        var updateArrows = function () {
          var max = track.scrollWidth - track.clientWidth;
          if (prev) {
            if (track.scrollLeft <= 0) { prev.setAttribute('disabled', ''); } else { prev.removeAttribute('disabled'); }
          }
          if (next) {
            if (track.scrollLeft >= max - 4) { next.setAttribute('disabled', ''); } else { next.removeAttribute('disabled'); }
          }
        };

        if (prev) { prev.addEventListener('click', function () { track.scrollBy({ left: -cardWidth(), behavior: 'smooth' }); }); }
        if (next) { next.addEventListener('click', function () { track.scrollBy({ left: cardWidth(), behavior: 'smooth' }); }); }
        track.addEventListener('scroll', function () {
          if (window.requestAnimationFrame) { window.requestAnimationFrame(updateArrows); } else { updateArrows(); }
        }, { passive: true });

        track.addEventListener('click', function (e) {
          var clip = e.target.closest('.lg-ugc__clip');
          if (!clip) return;
          var v = clip.querySelector('video');
          if (!v) return;
          if (v.paused) {
            var p = v.play();
            if (p && typeof p.then === 'function') { p.catch(function () {}); }
            clip.classList.add('is-playing');
          } else {
            v.pause();
            clip.classList.remove('is-playing');
          }
        });

        updateArrows();
        window.addEventListener('resize', function () {
          if (window.requestAnimationFrame) { window.requestAnimationFrame(updateArrows); } else { updateArrows(); }
        });
      });
    }
  };

  // 5) Reviews marquee — pause-on-hover handled in CSS; section settings drive loop duration.
  window.LiquidGold.reviews = {
    init: function (root) {
      var scope = root || document;
      Array.prototype.forEach.call(scope.querySelectorAll('[data-lg-reviews]'), function (el) {
        if (!once(el)) return;
        var track = el.querySelector('[data-lg-reviews-track]');
        if (!track) return;
        track.addEventListener('mouseenter', function () { track.style.animationPlayState = 'paused'; });
        track.addEventListener('mouseleave', function () { track.style.animationPlayState = 'running'; });
      });
    }
  };

  // 6) PDP dosing summary — Customize toggle expands a weight slider that recomputes the summary.
  window.LiquidGold.pdpDose = {
    init: function (root) {
      var scope = root || document;
      var compute = window.LiquidGold.doseSlider.compute;
      Array.prototype.forEach.call(scope.querySelectorAll('[data-lg-pdp-dose]'), function (el) {
        if (!once(el)) return;
        var trigger = el.querySelector('[data-lg-pdp-dose-trigger]');
        var panel   = el.querySelector('[data-lg-pdp-dose-panel]');
        var slider  = el.querySelector('input[type="range"]');
        var readout = el.querySelector('[data-lg-pdp-dose-readout]');
        var days    = el.querySelector('[data-lg-pdp-dose-days]');
        var pouchPriceAttr = el.getAttribute('data-lg-pdp-dose-pouch-price');
        var pouchPrice = pouchPriceAttr ? Number(pouchPriceAttr) : null;
        var dayCost  = el.querySelector('[data-lg-pdp-dose-day-cost]');
        var scoopCost= el.querySelector('[data-lg-pdp-dose-scoop-cost]');

        if (trigger && panel) {
          trigger.addEventListener('click', function () {
            var open = trigger.getAttribute('aria-expanded') === 'true';
            trigger.setAttribute('aria-expanded', String(!open));
            panel.hidden = open;
          });
        }

        if (slider) {
          var update = function () {
            var lb = Number(slider.value);
            var data = compute(lb);
            if (readout) { readout.textContent = String(lb); }
            if (days) { days.textContent = '~' + data.days; }
            if (pouchPrice && data.days > 0) {
              var perDay = pouchPrice / data.days;
              var perScoop = pouchPrice / 58; // 58 scoops per 290g pouch
              if (dayCost) { dayCost.textContent = '$' + perDay.toFixed(2); }
              if (scoopCost) { scoopCost.textContent = '$' + perScoop.toFixed(2); }
            }
          };
          slider.addEventListener('input', function () {
            if (window.requestAnimationFrame) { window.requestAnimationFrame(update); } else { update(); }
          });
          update();
        }
      });
    }
  };

  // LIQUID_GOLD: PDP 90-day timeline — sync rail fill, dog scrubber, and active phase card.
  window.LiquidGold.tl90 = {
    init: function (root) {
      var scope = root || document;
      Array.prototype.forEach.call(scope.querySelectorAll('[data-lg-tl90]'), function (el) {
        if (once(el)) { return; }
        var nodes  = el.querySelectorAll('[data-lg-tl90-node]');
        var phases = el.querySelectorAll('[data-lg-tl90-phase]');
        var fill   = el.querySelector('[data-lg-tl90-fill]');
        var dog    = el.querySelector('[data-lg-tl90-dog]');
        var dogWhen  = el.querySelector('[data-lg-tl90-dog-when]');
        var dogVital = el.querySelector('[data-lg-tl90-dog-vital]');
        if (!nodes.length || !phases.length) { return; }

        var autoplaySec = parseFloat(el.getAttribute('data-lg-tl90-autoplay'));
        if (isNaN(autoplaySec)) { autoplaySec = 4; }
        var autoplayMs = autoplaySec > 0 ? autoplaySec * 1000 : 0;
        var active = 0;
        var autoTimer = null;
        var userPaused = false;

        var setActive = function (i, fromAuto) {
          active = ((i % phases.length) + phases.length) % phases.length;
          var phase = phases[active];
          var pct = parseFloat(phase.getAttribute('data-lg-pct')) || 0;
          var when = phase.getAttribute('data-lg-when') || '';
          var vlabel = phase.getAttribute('data-lg-vital-label') || '';
          var vvalue = phase.getAttribute('data-lg-vital-value') || '';

          if (fill) { fill.style.width = pct + '%'; }
          if (dog)  { dog.style.left = pct + '%'; }
          if (dogWhen)  { dogWhen.textContent = when; }
          if (dogVital) { dogVital.textContent = vlabel + ' · ' + vvalue + '%'; }

          Array.prototype.forEach.call(nodes, function (n, ni) {
            n.classList.toggle('is-active', ni === active);
            n.classList.toggle('is-reached', ni <= active);
            n.setAttribute('aria-selected', ni === active ? 'true' : 'false');
          });

          Array.prototype.forEach.call(phases, function (p, pi) {
            p.classList.toggle('is-active', pi === active);
          });

          if (!fromAuto) { userPaused = true; stopAuto(); }
        };

        var stopAuto = function () {
          if (autoTimer) { clearTimeout(autoTimer); autoTimer = null; }
        };
        var queueAuto = function () {
          stopAuto();
          if (autoplayMs <= 0 || userPaused) { return; }
          autoTimer = setTimeout(function () {
            setActive(active + 1, true);
            queueAuto();
          }, autoplayMs);
        };

        Array.prototype.forEach.call(nodes, function (n, ni) {
          n.addEventListener('click', function () { setActive(ni); });
        });

        el.addEventListener('click', function (e) {
          var prev = e.target.closest && e.target.closest('[data-lg-tl90-prev]');
          var next = e.target.closest && e.target.closest('[data-lg-tl90-next]');
          if (prev) { setActive(active - 1); }
          else if (next) { setActive(active + 1); }
        });

        el.addEventListener('mouseenter', function () {
          if (autoTimer) { stopAuto(); }
        });
        el.addEventListener('mouseleave', function () {
          if (!userPaused) { queueAuto(); }
        });

        if (autoplayMs > 0 && typeof IntersectionObserver !== 'undefined') {
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                io.disconnect();
                queueAuto();
              }
            });
          }, { threshold: 0.35 });
          io.observe(el);
        }
      });
    }
  };

  function initAll(root) {
    window.LiquidGold.reveal.init(root);
    window.LiquidGold.hero.init(root);
    window.LiquidGold.doseSlider.init(root);
    window.LiquidGold.ugc.init(root);
    window.LiquidGold.reviews.init(root);
    window.LiquidGold.pdpDose.init(root);
    window.LiquidGold.tl90.init(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // LIQUID_GOLD: re-init on Shopify section editor events so previews stay live.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
